/**
 * Ship autopilot for the on-foot loop (vehicle-tiers V1: the GROUNDING POINT).
 *
 * The ship no longer climbs to orbit when you step out — it follows you down,
 * finds a spot a few metres off your shoulder, and LANDS. Landed is a terminal,
 * persistent state: the ship holds a frozen planet-local anchor (riding the
 * planet's spin exactly) until you board it or summon it somewhere else. The
 * summon (hold V, cooldown-gated in main.ts) re-enters the callback sweep from
 * wherever the ship is parked and lands it beside you again.
 *
 * THE WHOLE SIMULATION RUNS IN THE PLANET-LOCAL FRAME. The planet rotates and
 * orbits, carrying the player at ~50 m/s in world terms — in world space every
 * approach envelope chases a runaway target (the braking law `speed ∝ distance`
 * reaches equilibrium ~60 m out and the ship loiters there forever, drifting
 * away with the ground). In the planet's own frame the player is STATIONARY, so
 * the geometry below works as written; world pose and velocity are derived at
 * the end of each tick from the live pose.
 *
 * Motion shaping, three layers so nothing ever snaps:
 *  1. The callback yields an IDEAL travel direction + a speed target
 *     (great-circle pursuit with a sqrt braking envelope).
 *  2. A persistent PURSUIT direction turns toward the ideal at a capped rate —
 *     reversals become wide smooth arcs and the nose never whips around.
 *  3. Velocity eases toward pursuit·speed under an acceleration cap, and the
 *     landed handoff happens early at low speed so the position servo settles
 *     the last few metres instead of overshooting and snapping.
 * Ground clearance is enforced analytically (terrain + settlement rooflines) so
 * a descent never tunnels through a ridge or a tower.
 */
import * as THREE from "three";
import { WORLD_SCALE, cliffHit } from "@hyperlight/shared";
import type { Ship } from "./ship";
import type { Planet } from "../world/planet";

export type AutopilotPhase = "callback" | "landed";

/** Ship-origin height above terrain when parked — belly just clear of the dirt. */
const LAND_ALT = 2.8;
/** How far off the player's shoulder the ship sets down (m). */
const LAND_OFFSET = 14;
/** Minimum altitude over terrain during transit. */
const CLEARANCE = 35;
const CALLBACK_ACCEL = 160 * WORLD_SCALE;
const BRAKE = 80 * WORLD_SCALE;
/** Transit cruise (the old orbit speed × recall boost, unchanged tuning). */
const CALLBACK_BOOST = 3.5;
/** Angular window (rad) over which the callback blends transit height down. */
const DESCENT_WINDOW = 0.25;
/** How fast the pursuit direction may swing (rad/s); tightens close in. */
const TURN_RATE = 0.9;
/** Landed sway: a breath, not a hover — the ship is DOWN. */
const SWAY_V = 0.05;
const SWAY_H = 0.04;

export class Autopilot {
  phase: AutopilotPhase = "callback";

  private cruise: number;
  /** Fallback great-circle axis when ship and target are antipodal. */
  private axis = new THREE.Vector3();
  /** Planet-LOCAL ship position — the source of truth. */
  private lpos = new THREE.Vector3();
  /** Planet-LOCAL velocity (relative to the ground, not the world). */
  private vel = new THREE.Vector3();
  /** Rate-limited travel direction; the nose follows this, not raw vel. */
  private pursuit = new THREE.Vector3();
  /** FROZEN local anchor once landed — rides the frame, never drifts. */
  private landPoint = new THREE.Vector3();
  private landUp = new THREE.Vector3();
  private landT1 = new THREE.Vector3();
  private swayT = 0;
  private dir = new THREE.Vector3();
  private targetDir = new THREE.Vector3();
  private playerL = new THREE.Vector3();
  private tangent = new THREE.Vector3();
  private ideal = new THREE.Vector3();
  private desired = new THREE.Vector3();
  private gc = new THREE.Vector3();
  private off = new THREE.Vector3();
  private tmp = new THREE.Vector3();
  private tmp2 = new THREE.Vector3();
  private previousLocal = new THREE.Vector3();
  private cliffA = [0, 0, 0];
  private cliffB = [0, 0, 0];
  private cliffHit = cliffHit();
  private zero = new THREE.Vector3();
  private m = new THREE.Matrix4();
  private q = new THREE.Quaternion();

  constructor(
    private ship: Ship,
    private planet: Planet,
  ) {
    this.lpos.copy(ship.pos).sub(planet.pose.center).applyQuaternion(planet.pose.invQuat);
    this.vel.copy(ship.vel).applyQuaternion(planet.pose.invQuat);
    this.cruise = Math.min(480 * WORLD_SCALE, Math.max(180, planet.def.radius * 0.12));
    this.dir.copy(this.lpos).normalize();
    this.tmp.set(0, 0, -1).applyQuaternion(ship.quat).applyQuaternion(planet.pose.invQuat);
    this.tmp.addScaledVector(this.dir, -this.tmp.dot(this.dir));
    if (this.tmp.lengthSq() < 1e-6) this.tmp.set(0, 1, 0).cross(this.dir);
    this.axis.crossVectors(this.dir, this.tmp.normalize()).normalize();
    this.pursuit.copy(this.tmp);
  }

  /**
   * Restore a PARKED ship (login / warp-in): enter `landed` directly at a
   * persisted planet-local anchor, re-seated on the CURRENT terrain height so a
   * worldgen tweak between sessions can never bury the hull.
   */
  static landedAt(ship: Ship, planet: Planet, localPos: THREE.Vector3): Autopilot {
    const ap = new Autopilot(ship, planet);
    ap.enterLanded(ap.tmp.copy(localPos).normalize());
    ap.lpos.copy(ap.landPoint);
    ap.vel.set(0, 0, 0);
    ship.pos.copy(ap.landPoint).applyQuaternion(planet.pose.quat).add(planet.pose.center);
    ship.prevPos.copy(ship.pos);
    ship.vel.set(0, 0, 0);
    ship.speed = 0;
    return ap;
  }

  /** Fly to the player and land beside them (the summon, and the disembark
   *  follow-down). No-op while already inbound. */
  call(): void {
    if (this.phase !== "callback") this.phase = "callback";
  }

  /** The frozen planet-local anchor while parked, or null in transit — what
   *  persistPosition writes (already in the SPINNING frame, which is the whole
   *  fix for ships drifting across the planet between sessions). */
  landedAnchor(): THREE.Vector3 | null {
    return this.phase === "landed" ? this.landPoint : null;
  }

  /** Freeze the anchor on the current terrain under `upDir` and go landed. */
  private enterLanded(upDir: THREE.Vector3): void {
    this.phase = "landed";
    this.landUp.copy(upDir);
    this.landPoint.copy(upDir).multiplyScalar(this.surfClear(upDir, 7) + LAND_ALT);
    this.landT1.set(1, 0, 0).cross(this.landUp);
    if (this.landT1.lengthSq() < 1e-6) this.landT1.set(0, 0, 1).cross(this.landUp);
    this.landT1.normalize();
  }

  /** Stop-in-`remaining`-meters speed envelope (capped at `max`). */
  private approachSpeed(remaining: number, max: number): number {
    return Math.min(max, Math.sqrt(2 * BRAKE * Math.max(0, remaining)) + 2);
  }

  /** Swing the pursuit direction toward `ideal`, rate-capped. */
  private steer(dt: number, agility: number): void {
    const ang = this.pursuit.angleTo(this.ideal);
    if (ang < 1e-4) return;
    const k = Math.min(1, (TURN_RATE * agility * dt) / ang);
    this.pursuit.lerp(this.ideal, k).normalize();
  }

  /** Terrain-or-sea radius along a LOCAL unit direction. */
  private surfR(d: THREE.Vector3): number {
    return Math.max(
      this.planet.sampler.surfaceRadius(d.x, d.y, d.z),
      this.planet.def.seaRadius ?? -Infinity,
    );
  }

  /** surfR plus the local ROOFLINE over a settlement — transit must clear
   *  towers; the landing spot probes tight so it still fits a street. */
  private surfClear(d: THREE.Vector3, probeR: number): number {
    const base = this.surfR(d);
    let top = this.planet.settlementColliders?.clearanceRadius(d.x, d.y, d.z, base, probeR) ?? base;
    // Autopilot deliberately routes OVER finite geology. Unlike foot support,
    // this is an outside landing/flight plan, so the topmost ledge is the obstacle.
    const field = this.planet.terrainSolids;
    if (field?.solids.length) {
      const high = this.planet.def.radius * 1.5 + 2000;
      this.cliffA[0] = d.x * high; this.cliffA[1] = d.y * high; this.cliffA[2] = d.z * high;
      const hit = field.supportBelow(this.cliffA, Math.max(0, high - base), this.cliffHit);
      if (hit) top = Math.max(top, Math.hypot(...hit.point));
    }
    return top;
  }

  update(dt: number, playerPos: THREE.Vector3): void {
    if (this.planet.terrainSolidsReady === false && this.lpos.length() - this.planet.def.radius <= 9000) return;
    const ship = this.ship;
    const pose = this.planet.pose;
    ship.prevPos.copy(ship.pos);
    ship.prevQuat.copy(ship.quat);
    this.swayT += dt;

    // The player, brought into the frame where they stand still.
    this.playerL.copy(playerPos).sub(pose.center).applyQuaternion(pose.invQuat);

    this.dir.copy(this.lpos);
    let radius = this.dir.length() || 1;
    this.dir.divideScalar(radius);

    let minAlt = CLEARANCE;

    if (this.phase === "callback") {
      // Land OFF the player's shoulder, not on their head: shift the target
      // point sideways along a stable tangent before normalizing.
      this.off.crossVectors(this.axis, this.tmp.copy(this.playerL).normalize());
      if (this.off.lengthSq() < 1e-6) this.off.set(1, 0, 0).cross(this.tmp);
      this.targetDir.copy(this.playerL).addScaledVector(this.off.normalize(), LAND_OFFSET).normalize();
      const ang = this.dir.angleTo(this.targetDir);
      const landR = this.surfClear(this.targetDir, 7) + LAND_ALT;
      const k = Math.min(1, ang / DESCENT_WINDOW);
      const transitR = Math.max(landR, this.surfClear(this.dir, 40) + CLEARANCE);
      const rT = landR + (transitR - landR) * k * k;
      const arc = ang * radius;
      const radialErr = rT - radius;
      const remaining = arc + Math.abs(radius - landR);

      this.gc.crossVectors(this.dir, this.targetDir);
      if (this.gc.lengthSq() < 1e-9) this.gc.copy(this.axis);
      this.tangent.crossVectors(this.gc.normalize(), this.dir).normalize();
      this.ideal.copy(this.tangent).multiplyScalar(arc).addScaledVector(this.dir, radialErr);
      if (this.ideal.lengthSq() < 1e-6) this.ideal.copy(this.tangent);
      this.ideal.normalize();

      // True 3D distance to the landing point governs the endgame: speed must
      // fall LINEARLY with it so the turning circle shrinks faster than the
      // distance — otherwise the ship cannot wrap onto the point and loiters.
      this.tmp.copy(this.targetDir).multiplyScalar(landR).sub(this.lpos);
      const dist3 = this.tmp.length();
      this.steer(dt, 3 + 4 * Math.max(0, 1 - dist3 / 80));
      let speedT = this.approachSpeed(remaining, this.cruise * CALLBACK_BOOST);
      if (dist3 < 80) speedT = Math.min(speedT, Math.max(4, dist3 * 0.8));
      // Brake INTO a turnaround: while the pursuit still points away from the
      // target, hold near-pivot speed.
      speedT *= 0.15 + 0.85 * Math.max(0, this.pursuit.dot(this.ideal));
      this.desired.copy(this.pursuit).multiplyScalar(speedT);

      minAlt = LAND_ALT + (CLEARANCE - LAND_ALT) * Math.min(1, ang / 0.08);
      if (dist3 < 6 && this.vel.length() < 16) {
        // Early, low-speed handoff: the landed servo settles the last metres.
        // The anchor FREEZES here (local frame): the parked ship holds this
        // spot however the planet turns and wherever the player walks.
        this.enterLanded(this.targetDir);
      }
    } else {
      // Landed: a gentle position servo around the frozen local anchor, with a
      // breath of thruster sway so the hull reads alive, not stuck.
      this.tmp
        .copy(this.landPoint)
        .addScaledVector(this.landUp, Math.sin(this.swayT * 2.1) * SWAY_V)
        .addScaledVector(this.landT1, Math.sin(this.swayT * 1.3 + 1.7) * SWAY_H);
      this.desired.copy(this.tmp).sub(this.lpos).multiplyScalar(1.4);
      const s = this.desired.length();
      if (s > 12) this.desired.multiplyScalar(12 / s);
      minAlt = LAND_ALT - 1;
    }

    // --- Acceleration-limited integration (local frame) ---
    this.tmp.copy(this.desired).sub(this.vel);
    const dv = this.tmp.length();
    const maxDv = CALLBACK_ACCEL * dt;
    if (dv > 1e-6) this.vel.addScaledVector(this.tmp, Math.min(1, maxDv / dv));
    this.previousLocal.copy(this.lpos);
    this.lpos.addScaledVector(this.vel, dt);

    // --- Analytic ground safety (terrain + settlement rooflines) ---
    this.dir.copy(this.lpos);
    radius = this.dir.length() || 1;
    this.dir.divideScalar(radius);
    const probe = Math.max(7, Math.min(60, this.vel.length() * 0.8));
    const ground = this.surfClear(this.dir, probe);
    if (radius < ground + minAlt) {
      radius = ground + minAlt;
      this.lpos.copy(this.dir).multiplyScalar(radius);
      const vn = this.vel.dot(this.dir);
      if (vn < 0) this.vel.addScaledVector(this.dir, -vn);
    }
    // Include both lateral motion and any radial clearance correction in one
    // exact sweep. An under-roof recall must stop, not teleport through its roof.
    const field = this.planet.terrainSolids;
    if (field?.solids.length) {
      this.previousLocal.toArray(this.cliffA); this.lpos.toArray(this.cliffB);
      const hit = field.sphereSweep(this.cliffA, this.cliffB, 2.7, this.cliffHit);
      if (hit) {
        this.lpos.lerpVectors(this.previousLocal, this.lpos, Math.max(0, hit.t - .0001));
        this.tmp.set(...hit.normal);
        this.lpos.addScaledVector(this.tmp, hit.penetration + .005);
        const inward = this.vel.dot(this.tmp);
        if (inward < 0) this.vel.addScaledVector(this.tmp, -inward);
        radius = this.lpos.length(); this.dir.copy(this.lpos).divideScalar(radius || 1);
      }
    }

    // --- Back to the world: pose from the live frame ---
    ship.pos.copy(this.lpos).applyQuaternion(pose.quat).add(pose.center);
    // ship.vel stays the ship's OWN velocity (ground-relative, world axes):
    // the carry system moves passengers positionally, so folding the frame
    // velocity in here would double-count it the moment the player boards.
    ship.vel.copy(this.vel).applyQuaternion(pose.quat);
    ship.speed = this.vel.length();
    ship.surfaceDist = radius - ground;

    // The nose follows the smoothed pursuit direction — pitch-limited, because
    // the VTOL thrusters carry steep descents: the hull never points at dirt.
    const dirW = this.tmp2.copy(this.dir).applyQuaternion(pose.quat);
    if (this.phase === "landed") {
      // Level out: nose to the horizon, wings flat — a parked craft.
      this.tmp.set(0, 0, -1).applyQuaternion(ship.quat);
      this.tmp.addScaledVector(dirW, -this.tmp.dot(dirW));
      if (this.tmp.lengthSq() < 1e-6) this.tmp.copy(this.landT1).applyQuaternion(pose.quat);
      this.m.lookAt(this.zero.set(0, 0, 0), this.tmp.normalize(), dirW);
      this.q.setFromRotationMatrix(this.m);
      ship.quat.rotateTowards(this.q, 1.2 * dt);
    } else if (ship.speed > 2) {
      const vert = this.pursuit.dot(this.dir);
      this.tmp.copy(this.pursuit).addScaledVector(this.dir, -vert);
      if (this.tmp.lengthSq() < 1e-4) this.tmp.copy(this.tangent); // pure vertical leg
      this.tmp.normalize().addScaledVector(this.dir, Math.max(-0.6, Math.min(0.6, vert)));
      this.tmp.applyQuaternion(pose.quat);
      this.m.lookAt(this.zero.set(0, 0, 0), this.tmp.normalize(), dirW);
      this.q.setFromRotationMatrix(this.m);
      ship.quat.rotateTowards(this.q, 2 * dt);
    }
  }
}
